import React, { useMemo, useState } from "react";
import { Search, X, SlidersHorizontal, Check } from "lucide-react";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "../components/ui/sheet";
import Reveal from "../components/Reveal";
import ArtistCard from "../components/ArtistCard";
import { artists, styleFilters, studio } from "../mock";
import { cn } from "../lib/utils";

const FilterChips = ({ filter, setFilter, counts }) => (
  <div className="hidden flex-wrap gap-2 lg:flex" data-testid="artists-filter-chips">
    {styleFilters.map((f) => {
      const active = filter === f.id;
      return (
        <button
          key={f.id}
          type="button"
          onClick={() => setFilter(f.id)}
          className={cn(
            "flex items-center gap-2 border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300",
            active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-transparent text-foreground/80 hover:border-foreground/50 hover:text-foreground"
          )}
          aria-pressed={active}
          data-testid={`filter-${f.id}`}
        >
          {f.label}
          <span className={cn("text-[10px]", active ? "text-primary-foreground/80" : "text-muted-foreground")}>{counts[f.id]}</span>
        </button>
      );
    })}
  </div>
);

/** Mobile: one compact button that opens the style filters in a bottom sheet. */
const MobileFilters = ({ filter, setFilter, counts }) => {
  const [open, setOpen] = useState(false);
  const activeLabel = styleFilters.find((f) => f.id === filter)?.label || "All";
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-10 shrink-0 items-center gap-2 border px-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors lg:hidden",
            filter !== "all" ? "border-primary text-primary" : "border-border text-foreground/80"
          )}
          aria-label="Open style filters"
          data-testid="artists-filter-menu"
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="max-w-[110px] truncate">{filter === "all" ? "Filter" : activeLabel}</span>
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-none border-border bg-background px-0 pb-8 pt-6" data-testid="artists-filter-sheet">
        <div className="container-x">
          <SheetTitle className="display text-3xl uppercase leading-none tracking-wide">Filter by style</SheetTitle>
          <SheetDescription className="mt-1 text-xs text-muted-foreground">Pick a style to narrow the roster.</SheetDescription>
          <ul className="mt-5 divide-y divide-border border-y border-border">
            {styleFilters.map((f) => {
              const active = filter === f.id;
              return (
                <li key={f.id}>
                  <SheetClose asChild>
                    <button
                      type="button"
                      onClick={() => setFilter(f.id)}
                      className={cn("flex w-full items-center justify-between py-3.5 text-left text-sm font-semibold uppercase tracking-[0.16em]", active ? "text-primary" : "text-foreground/85")}
                      aria-pressed={active}
                      data-testid={`filter-${f.id}`}
                    >
                      <span className="flex items-center gap-3">
                        {active ? <Check className="h-4 w-4" /> : <span className="h-4 w-4" />}
                        {f.label}
                      </span>
                      <span className="text-xs text-muted-foreground">{counts[f.id]}</span>
                    </button>
                  </SheetClose>
                </li>
              );
            })}
          </ul>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Artists = () => {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return artists.filter((a) => {
      const matchesFilter = filter === "all" || a.tags.includes(filter);
      const matchesQuery =
        !q || a.name.toLowerCase().includes(q) || a.specialty.toLowerCase().includes(q) || a.bookingLabel.toLowerCase().includes(q);
      return matchesFilter && matchesQuery;
    });
  }, [filter, query]);

  const counts = useMemo(() => {
    const map = { all: artists.length };
    styleFilters.forEach((f) => {
      if (f.id !== "all") map[f.id] = artists.filter((a) => a.tags.includes(f.id)).length;
    });
    return map;
  }, []);

  return (
    <main className="pt-[76px]" data-testid="artists-page">
      {/* Header */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="container-x pb-16 pt-20 lg:pb-20 lg:pt-28">
          <p className="eyebrow hero-in hero-in-1 flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-primary" />
            {artists.length} resident artists
          </p>
          <h1 className="display hero-in hero-in-2 mt-6 text-[clamp(4rem,12vw,12rem)] uppercase leading-[0.82] tracking-wide">
            Our <span className="text-stroke">Artists</span>
          </h1>
          <p className="hero-in hero-in-3 mt-8 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            <span className="font-semibold text-primary">To Book: </span>
            {studio.bookingNote} Each artist manages their own bookings through their website or Instagram.
          </p>
        </div>
      </section>

      {/* Filters: chips on desktop, a single filter button + search on mobile */}
      <section className="sticky top-[76px] z-30 border-b border-border bg-background/85 backdrop-blur-xl" data-testid="artists-filters">
        <div className="container-x flex items-center gap-3 py-3 lg:justify-between lg:py-4">
          <FilterChips filter={filter} setFilter={setFilter} counts={counts} />
          <MobileFilters filter={filter} setFilter={setFilter} counts={counts} />
          <div className="relative min-w-0 flex-1 lg:w-72 lg:flex-none">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search artists or styles"
              className="h-10 rounded-none border-border bg-transparent pl-9 pr-9 text-sm placeholder:text-muted-foreground focus-visible:ring-primary"
              data-testid="artists-search"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
                data-testid="artists-search-clear"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="container-x py-16 lg:py-24">
        <p className="mb-10 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground" data-testid="artists-count">
          {filtered.length} {filtered.length === 1 ? "artist" : "artists"}
          {filter !== "all" && ` · ${styleFilters.find((f) => f.id === filter)?.label}`}
        </p>

        {filtered.length === 0 ? (
          <div className="border border-dashed border-border py-24 text-center" data-testid="artists-empty">
            <p className="display text-4xl uppercase">No artists found</p>
            <p className="mt-3 text-sm text-muted-foreground">Try a different style or clear your search.</p>
            <button
              type="button"
              onClick={() => {
                setFilter("all");
                setQuery("");
              }}
              className="mt-6 border border-border px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] hover:border-primary hover:text-primary"
              data-testid="artists-reset"
            >
              Reset filters
            </button>
          </div>
        ) : (
          <div key={`${filter}-${query}`} className="grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((artist, i) => (
              <Reveal key={artist.id} delay={(i % 4) * 70}>
                <ArtistCard artist={artist} index={artists.indexOf(artist)} />
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default Artists;
